import { z } from 'zod';
import { hexColorSchema, idSchema, isoDateTimeSchema } from '../../common.js';
import { guestIdSchema, guestNameSchema } from './guestCore.js';
import { gameKindSchema, gameMetaSchema } from './index.js';
/**
 * Guest play links (WS-23), part two: the REST wire contracts.
 *
 * Separate from `guestCore.ts` because these need `gameMetaSchema`, which lives in the games
 * barrel — see that file's note on why the dependency runs one way only. Re-exported from
 * `packages/shared/src/modules/index.ts` rather than from `games/index.ts`, which is what
 * keeps the edge acyclic.
 *
 * Secrets never appear in any of these shapes except once, in the mint response: only token
 * *hashes* are persisted (`migrations/games/001_game_guest_tokens.sql`).
 */
/** `POST /api/games/:gameId/guest-link` — authenticated (member session). */
export const mintGuestLinkResponseSchema = z.object({
    /** The only moment the raw token exists anywhere but the recipient's URL bar. */
    token: z.string().min(1),
    /** Public, not secret: the creator writes it straight into `playerIds` as seat 2. */
    guestId: guestIdSchema,
});
/** `GET /api/games/:gameId/guest-link` — authenticated; never echoes the token back. */
export const guestLinkStatusResponseSchema = z.object({
    exists: z.boolean(),
    guestId: guestIdSchema.nullable(),
    createdAt: isoDateTimeSchema.nullable(),
    claimedAt: isoDateTimeSchema.nullable(),
    guestName: guestNameSchema.nullable(),
});
/** `POST /api/games/play/:token` body — the name is skippable, hence optional. */
export const claimGuestLinkRequestSchema = z.object({ name: guestNameSchema.optional() }).strict();
/**
 * `GET /api/games/play/:token`, and the response to a successful claim.
 *
 * `state` is the whole of what the public page learns, and the three values are picked so a
 * prober learns as little as possible. An unknown token is a flat 404 with no body at all.
 * `taken` carries **no** game details — not the kind, not the title, not who minted it — so
 * "somebody already has this seat" cannot be turned into a read of household content. Only a
 * seat that is genuinely yours (`open` before you claim it, `mine` after) unlocks them.
 */
export const guestSeatStateSchema = z.enum(['open', 'mine', 'taken']);
export const guestSeatResponseSchema = z.object({
    state: guestSeatStateSchema,
    /**
     * The registry entry, handed over the wire because a guest cannot open the `games` doc to
     * read it. This is the *only* `GameMeta` they ever receive — one game, the one they were
     * invited to. It is a snapshot: the live truth of a match is its move log in `game:<id>`,
     * which every board derives from anyway, so a stale `status` here costs nothing.
     */
    game: gameMetaSchema.optional(),
    /** Display name of the member who sent the link — "Dan asked you to play". */
    invitedBy: z.string().min(1).max(100).optional(),
    invitedByColor: hexColorSchema.optional(),
    /** The inviting member's id — lets the guest's board name them ("Dan") without ever
     * opening the household doc. Not a secret: it is already in `playerIds`. */
    invitedById: idSchema.optional(),
    /** Present once the seat is `mine`: who the board should think you are. */
    guestId: guestIdSchema.optional(),
    guestName: guestNameSchema.optional(),
    /**
     * Friend of the family: on an `open` link, the name this browser's existing guest session
     * already goes by, so a returning friend sees "welcome back" and a pre-filled name instead
     * of being asked who they are again. Absent for a first-time visitor.
     */
    knownAs: guestNameSchema.optional(),
});
/* -------------------------------------------------------------------------- */
/* Friend of the family — the guest lounge                                     */
/* -------------------------------------------------------------------------- */
/**
 * A guest who has claimed a seat keeps their identity (the "friend pass"): one long-lived
 * guest session, authorised for every match they hold a seat in with this household. The
 * lounge (`/play/lounge`) is where they see those matches and start another one.
 *
 * Light limit on how many unfinished games a friend may have going at once. Generous enough
 * never to be felt by a real person, small enough that a stuck-down button can't flood the
 * family's games list.
 */
export const FRIEND_MAX_ACTIVE_GAMES = 6;
/** A household member a friend has played with — the people they can challenge again. */
export const friendMemberSchema = z.object({
    id: idSchema,
    name: z.string().min(1).max(100),
    color: hexColorSchema,
});
export const friendGameSchema = z.object({
    game: gameMetaSchema,
    /** The id this friend plays as in *this* match (seat ids differ between links). */
    seatId: guestIdSchema,
    yourMove: z.boolean(),
    finished: z.boolean(),
});
/** `GET /api/games/friend` — guest session only. Also quietly renews the friend pass. */
export const friendLoungeResponseSchema = z.object({
    guestId: guestIdSchema,
    guestName: guestNameSchema,
    members: z.array(friendMemberSchema),
    /** Newest first. Only matches this friend sits in — never anything else in the house. */
    games: z.array(friendGameSchema),
    maxActive: z.number().int().positive(),
});
/** `POST /api/games/friend/games` — a friend challenges a member they have played with. */
export const startFriendGameRequestSchema = z
    .object({ kind: gameKindSchema, memberId: idSchema })
    .strict();
/**
 * `POST /api/games/:gameId/guest-seat` — member session. A member's Rematch against a guest
 * creates a new registry entry with the guest's seat id in it; this lets that friend open
 * the new match too (the guest can't be told about it any other way).
 */
export const attachGuestSeatRequestSchema = z.object({ seatId: guestIdSchema }).strict();
//# sourceMappingURL=guest.js.map